/**
 * PLC Control Popup System
 * 
 * Endüstriyel ekipman kontrolü için modal popup yöneticisi.
 * Motor, vana, pompa gibi cihazları PLC üzerinden kontrol eder.
 * 
 * Popup Tipleri:
 *   control-setbool      → Basit Bool yazma (Start/Stop)
 *   control-setreal      → Basit Real değer yazma
 *   control-onoff        → Motor Start/Stop (suffix: _Mstr)
 *   control-onoff-auto   → Modlu motor (suffix: _Au, _Mstr)
 *   control-proportional → Oransal kontrol (suffix: _Aval, _Mval, _Au, _Mstr, _Res)
 *   control-setpoint     → Tek set değeri (suffix: _Aval)
 */

(function() {
  'use strict';

  // ============================================================
  // API Helper
  // ============================================================

  const API_BASE = '/admin/api/nexbus';

  // Auth token ve kullanıcı bilgisi (auth.js'den)
  function getAuthHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    if (window.Auth && typeof window.Auth.getToken === 'function') {
      const token = window.Auth.getToken();
      if (token) headers['Authorization'] = 'Bearer ' + token;
    }
    return headers;
  }

  function getCurrentUser() {
    if (window.Auth && typeof window.Auth.getUserInfo === 'function') {
      const info = window.Auth.getUserInfo();
      return (info && info.UserName) || 'operator';
    }
    return 'operator';
  }

  async function plcWriteBool(tagName, value, user, oldValue, displayName) {
    const res = await fetch(`${API_BASE}/plc/write/bool`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ tag_name: tagName, value: !!value, user: user || getCurrentUser(), old_value: oldValue, display_name: displayName })
    });
    if (res.status === 401) { showNotify('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.', 'error'); return { success: false, error: 'Unauthorized' }; }
    const result = await res.json();
    if (result && result.success && window.latestTagValues) window.latestTagValues.set(tagName, !!value);
    return result;
  }

  async function plcWriteReal(tagName, value, user, oldValue, displayName) {
    const res = await fetch(`${API_BASE}/plc/write/real`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ tag_name: tagName, value: parseFloat(value), user: user || getCurrentUser(), old_value: oldValue, display_name: displayName })
    });
    if (res.status === 401) { showNotify('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.', 'error'); return { success: false, error: 'Unauthorized' }; }
    const result = await res.json();
    if (result && result.success && window.latestTagValues) window.latestTagValues.set(tagName, parseFloat(value));
    return result;
  }

  async function plcWriteInt(tagName, value, user, oldValue, displayName) {
    const res = await fetch(`${API_BASE}/plc/write/int`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ tag_name: tagName, value: parseInt(value, 10), user: user || getCurrentUser(), old_value: oldValue, display_name: displayName })
    });
    if (res.status === 401) { showNotify('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.', 'error'); return { success: false, error: 'Unauthorized' }; }
    const result = await res.json();
    if (result && result.success && window.latestTagValues) window.latestTagValues.set(tagName, parseInt(value, 10));
    return result;
  }

  async function plcWriteBatch(writes, user) {
    // writes: [{tag_name, value, data_type}]
    const res = await fetch(`${API_BASE}/plc/write/batch`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ writes, user: user || getCurrentUser() })
    });
    if (res.status === 401) { showNotify('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.', 'error'); return { success: false, error: 'Unauthorized' }; }
    return res.json();
  }

  async function plcReadTags(tags) {
    const res = await fetch(`${API_BASE}/plc/read`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ tags })
    });
    return res.json();
  }

  // ============================================================
  // Toast Notification
  // ============================================================

  function showNotify(message, type) {
    // type: 'success' | 'error' | 'info' | 'warning'
    const colors = { success: '#2eb85c', error: '#e55353', info: '#3399ff', warning: '#f9b115' };
    const icons = { success: 'fa-check-circle', error: 'fa-times-circle', info: 'fa-info-circle', warning: 'fa-exclamation-triangle' };

    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed; top: 20px; right: 20px; z-index: 200000;
      background: ${colors[type] || colors.info}; color: #fff;
      padding: 12px 20px; border-radius: 6px; font-size: 14px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3); display: flex; align-items: center; gap: 8px;
      animation: controlPopupSlideIn 0.3s ease; max-width: 400px;
    `;
    toast.innerHTML = `<i class="fas ${icons[type] || icons.info}"></i> ${escapeHtml(message)}`;
    document.body.appendChild(toast);
    setTimeout(() => { toast.style.opacity = '0'; toast.style.transition = 'opacity 0.3s'; }, 2500);
    setTimeout(() => toast.remove(), 3000);
  }

  function escapeHtml(str) {
    const d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  // ============================================================
  // Popup Registry — tüm popup tipleri burada kayıtlı
  // ============================================================

  const POPUP_TYPES = {};

  function registerPopupType(name, builder) {
    POPUP_TYPES[name] = builder;
  }

  // ============================================================
  // ControlPopupManager
  // ============================================================

  class ControlPopupManager {
    constructor() {
      this._modalEl = null;
      this._backdrop = null;
      this._currentConfig = null;
      this._injectStyles();
    }

    /**
     * Popup aç
     * @param {string} popupType - Popup tipi (control-setbool, control-onoff, vb.)
     * @param {object} config - Popup konfigürasyonu
     * @param {string} config.rootTag - Cihaz ana tag adı
     * @param {string} [config.title] - Başlık
     * @param {number} [config.authLevel] - Yetki seviyesi
     * @param {object} [config.suffixes] - Özel suffix'ler
     * @param {object} [config.labels] - Özel buton etiketleri
     */
    async open(popupType, config) {
      if (!POPUP_TYPES[popupType]) {
        showNotify(`Bilinmeyen popup tipi: ${popupType}`, 'error');
        return;
      }

      // Auth kontrolü — giriş yapmamış kullanıcılar popup açamaz
      if (window.Auth && typeof window.Auth.isAuthenticated === 'function') {
        if (!window.Auth.isAuthenticated()) {
          showNotify('Bu işlem için giriş yapmanız gerekiyor', 'warning');
          return;
        }
      }

      this._currentConfig = config;
      this.close(); // Önceki popup'ı kapat

      const builder = POPUP_TYPES[popupType];
      const content = await builder(config, this);
      this._show(config.title || config.rootTag || 'Kontrol', content);
    }

    /** Modal göster */
    _show(title, bodyHtml) {
      // Backdrop
      this._backdrop = document.createElement('div');
      this._backdrop.className = 'cpopup-backdrop';
      this._backdrop.onclick = () => this.close();
      document.body.appendChild(this._backdrop);

      // Modal
      this._modalEl = document.createElement('div');
      this._modalEl.className = 'cpopup-modal';
      this._modalEl.innerHTML = `
        <div class="cpopup-header">
          <span class="cpopup-title">${escapeHtml(title)}</span>
          <button class="cpopup-close" title="Kapat">&times;</button>
        </div>
        <div class="cpopup-body">${bodyHtml}</div>
      `;
      document.body.appendChild(this._modalEl);

      // innerHTML ile eklenen <script> tagları otomatik çalışmaz — manuel çalıştır
      this._modalEl.querySelectorAll('script').forEach(oldScript => {
        const newScript = document.createElement('script');
        newScript.textContent = oldScript.textContent;
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });

      // Close button
      this._modalEl.querySelector('.cpopup-close').onclick = () => this.close();

      // Animasyon
      requestAnimationFrame(() => {
        this._backdrop.classList.add('visible');
        this._modalEl.classList.add('visible');
      });
    }

    /** Modal kapat */
    close() {
      if (this._modalEl) {
        this._modalEl.remove();
        this._modalEl = null;
      }
      if (this._backdrop) {
        this._backdrop.remove();
        this._backdrop = null;
      }
      this._currentConfig = null;
    }

    /** Mevcut modal body'sine erişim */
    getBody() {
      return this._modalEl ? this._modalEl.querySelector('.cpopup-body') : null;
    }

    /** CSS injection (tek sefer) */
    _injectStyles() {
      if (document.getElementById('cpopup-styles')) return;
      const style = document.createElement('style');
      style.id = 'cpopup-styles';
      style.textContent = `
        @keyframes controlPopupSlideIn {
          from { transform: translateX(60px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .cpopup-backdrop {
          position: fixed; inset: 0; background: rgba(0,0,0,0.5);
          z-index: 100000; opacity: 0; transition: opacity 0.2s;
        }
        .cpopup-backdrop.visible { opacity: 1; }
        .cpopup-modal {
          position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%) scale(0.9);
          z-index: 100001; background: #1e2130; color: #e4e6eb; border-radius: 10px;
          min-width: 320px; max-width: 90vw; box-shadow: 0 8px 32px rgba(0,0,0,0.5);
          opacity: 0; transition: all 0.25s ease;
        }
        .cpopup-modal.visible { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        .cpopup-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 14px 18px; border-bottom: 1px solid #333654;
          background: #262940; border-radius: 10px 10px 0 0;
        }
        .cpopup-title { font-size: 16px; font-weight: 600; }
        .cpopup-close {
          background: none; border: none; color: #aaa; font-size: 22px; cursor: pointer;
          width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
          border-radius: 4px;
        }
        .cpopup-close:hover { background: #e55353; color: #fff; }
        .cpopup-body { padding: 18px; }

        /* Kontrol butonları */
        .cpopup-btn-row { display: flex; gap: 10px; justify-content: center; margin-bottom: 14px; }
        .cpopup-btn {
          padding: 10px 24px; border: none; border-radius: 6px; font-size: 14px; font-weight: 600;
          cursor: pointer; min-width: 100px; transition: all 0.15s; color: #fff;
          display: flex; align-items: center; justify-content: center; gap: 6px;
        }
        .cpopup-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .cpopup-btn-start { background: #2eb85c; }
        .cpopup-btn-start:hover:not(:disabled) { background: #25a34e; }
        .cpopup-btn-start.active { background: #2eb85c; box-shadow: 0 0 12px rgba(46,184,92,0.5); }
        .cpopup-btn-stop { background: #e55353; }
        .cpopup-btn-stop:hover:not(:disabled) { background: #d13e3e; }
        .cpopup-btn-stop.active { background: #e55353; box-shadow: 0 0 12px rgba(229,83,83,0.5); }
        .cpopup-btn-auto { background: #3399ff; }
        .cpopup-btn-auto:hover:not(:disabled) { background: #2280e0; }
        .cpopup-btn-auto.active { background: #3399ff; box-shadow: 0 0 12px rgba(51,153,255,0.5); }
        .cpopup-btn-man { background: #f9b115; color: #1e2130; }
        .cpopup-btn-man:hover:not(:disabled) { background: #e0a00e; }
        .cpopup-btn-man.active { background: #f9b115; box-shadow: 0 0 12px rgba(249,177,21,0.5); color: #1e2130; }
        .cpopup-btn-reset { background: #636f83; }
        .cpopup-btn-reset:hover:not(:disabled) { background: #555f70; }
        .cpopup-btn-apply { background: #321fdb; }
        .cpopup-btn-apply:hover:not(:disabled) { background: #2819b0; }

        /* Input alanları */
        .cpopup-input-group { margin-bottom: 14px; }
        .cpopup-input-group label {
          display: block; font-size: 12px; color: #8a8ea8; margin-bottom: 4px; text-transform: uppercase;
        }
        .cpopup-input-group input {
          width: 100%; padding: 8px 12px; border: 1px solid #3a3d52; border-radius: 6px;
          background: #2a2d3e; color: #e4e6eb; font-size: 16px; outline: none;
        }
        .cpopup-input-group input:focus { border-color: #321fdb; }

        /* Spinner */
        .cpopup-spinner {
          width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.3);
          border-top-color: #fff; border-radius: 50%;
          animation: cpopupSpin 0.6s linear infinite; display: inline-block;
        }
        @keyframes cpopupSpin { to { transform: rotate(360deg); } }

        /* Ayırıcı */
        .cpopup-divider { border-top: 1px solid #333654; margin: 14px 0; }

        /* Footer */
        .cpopup-footer { display: flex; gap: 10px; justify-content: center; margin-top: 14px; }

        /* Inactive button (toggled OFF state) */
        .cpopup-btn-inactive { background: #3a3d52 !important; box-shadow: none !important; }

        /* Onay dialogu */
        .cpopup-confirm-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.6);
          z-index: 200001; display: flex; align-items: center; justify-content: center;
        }
        .cpopup-confirm-box {
          background: #1e2130; border-radius: 10px; padding: 24px; min-width: 300px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.5); text-align: center; color: #e4e6eb;
        }
        .cpopup-confirm-box .cpopup-confirm-msg { font-size: 15px; margin-bottom: 18px; }
        .cpopup-confirm-box .cpopup-confirm-actions { display: flex; gap: 10px; justify-content: center; }

        /* Validation hatası */
        .cpopup-input-error { border-color: #e55353 !important; }
        .cpopup-error-text { color: #e55353; font-size: 11px; margin-top: 2px; }
      `;
      (document.head || document.documentElement).appendChild(style);
    }
  }

  // ============================================================
  // Onay Dialogu (Confirm)
  // ============================================================

  function showConfirm(message) {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'cpopup-confirm-overlay';
      overlay.innerHTML = `
        <div class="cpopup-confirm-box">
          <div class="cpopup-confirm-msg"><i class="fas fa-exclamation-triangle" style="color:#f9b115;margin-right:6px;"></i>${escapeHtml(message)}</div>
          <div class="cpopup-confirm-actions">
            <button class="cpopup-btn cpopup-btn-stop" id="cpopup-confirm-no" style="min-width:80px;padding:8px 16px;">İptal</button>
            <button class="cpopup-btn cpopup-btn-start" id="cpopup-confirm-yes" style="min-width:80px;padding:8px 16px;">Onayla</button>
          </div>
        </div>
      `;
      document.body.appendChild(overlay);
      overlay.querySelector('#cpopup-confirm-yes').onclick = () => { overlay.remove(); resolve(true); };
      overlay.querySelector('#cpopup-confirm-no').onclick = () => { overlay.remove(); resolve(false); };
    });
  }

  // ============================================================
  // Helper: Buton durumunu spinner + doğrulama ile güncelle
  // ============================================================

  async function writeAndVerify(btn, writeFn, readTag, expectedValue, successMsg, errorMsg, skipConfirm) {
    // Onay dialogu (skipConfirm true değilse)
    if (!skipConfirm) {
      // Confirm mesajı: "gönderildi" → "gönderilecek", "geçildi" → "geçilecek" dönüşümü
      let confirmText = 'Bu PLC yazma işlemini onaylıyor musunuz?';
      if (successMsg) {
        const futureMsg = successMsg
          .replace(/gönderildi/gi, 'gönderilecek')
          .replace(/geçildi/gi, 'geçilecek')
          .replace(/başarıyla /gi, '');
        confirmText = `"${futureMsg}" işlemini gerçekleştirmek istiyor musunuz?`;
      }
      const confirmed = await showConfirm(confirmText);
      if (!confirmed) return false;
    }

    const originalHtml = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = `<span class="cpopup-spinner"></span>`;

    try {
      const result = await writeFn();
      if (!result.success) {
        showNotify(errorMsg || 'Yazma işlemi başarısız', 'error');
        btn.innerHTML = originalHtml;
        btn.disabled = false;
        return false;
      }

      // 2sn sonra PLC'den doğrulama oku
      await new Promise(r => setTimeout(r, 2000));

      if (readTag) {
        try {
          const readResult = await plcReadTags([readTag]);
          const tagVal = readResult.tags && readResult.tags[readTag];
          if (tagVal !== undefined) {
            const actualVal = typeof tagVal === 'object' ? tagVal.value : tagVal;
            // Doğrulama sonucunu dön
            btn.innerHTML = originalHtml;
            btn.disabled = false;
            return true; // Parent fonksiyon button state'i güncellesin
          }
        } catch (e) { /* doğrulama okunamadı, yine de başarılı say */ }
      }

      showNotify(successMsg || 'İşlem başarılı', 'success');
      btn.innerHTML = originalHtml;
      btn.disabled = false;
      return true;
    } catch (err) {
      showNotify(errorMsg || 'Bağlantı hatası', 'error');
      btn.innerHTML = originalHtml;
      btn.disabled = false;
      return false;
    }
  }

  // Helper: toggle button active states
  function setToggleState(btnA, btnB, aActive) {
    if (aActive) {
      btnA.classList.add('active');
      btnA.classList.remove('cpopup-btn-inactive');
      btnB.classList.remove('active');
      btnB.classList.add('cpopup-btn-inactive');
    } else {
      btnB.classList.add('active');
      btnB.classList.remove('cpopup-btn-inactive');
      btnA.classList.remove('active');
      btnA.classList.add('cpopup-btn-inactive');
    }
  }

  // Tag'ın güncel değerini oku — önce plcReadTags, yoksa latestTagValues fallback
  async function readTagValue(tag) {
    try {
      const r = await plcReadTags([tag]);
      if (r.tags && r.tags[tag] !== undefined) {
        const v = r.tags[tag];
        return typeof v === 'object' ? v.value : v;
      }
    } catch (e) { /* */ }
    // Fallback: dashboard'daki canlı veri cache'i
    if (window.latestTagValues) {
      const v = window.latestTagValues.get(tag);
      if (v !== undefined) {
        if (v && typeof v === 'object') return v.Value !== undefined ? v.Value : v.value;
        return v;
      }
    }
    return null;
  }

  // ============================================================
  // POPUP TİPİ: control-setbool
  // Basit Bool yazma — Start/Stop butonları
  // ============================================================

  registerPopupType('control-setbool', async (config) => {
    const tag = config.rootTag;
    const labels = config.labels || {};
    const displayName = config.title || config.displayName || tag;

    // Mevcut değeri oku
    let currentValue = await readTagValue(tag);

    const isBoolTrue = currentValue === true || currentValue === 'true' || currentValue === 1;
    const oldValStr = currentValue != null ? String(!!isBoolTrue) : 'null';

    return `
      <div class="cpopup-btn-row">
        <button class="cpopup-btn cpopup-btn-start ${isBoolTrue ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-set-true">
          <i class="fas fa-play"></i> ${escapeHtml(labels.startBtn || 'START')}
        </button>
        <button class="cpopup-btn cpopup-btn-stop ${!isBoolTrue ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-set-false">
          <i class="fas fa-stop"></i> ${escapeHtml(labels.stopBtn || 'STOP')}
        </button>
      </div>
      <div style="text-align:center; font-size:12px; color:#8a8ea8;">
        Tag: <span style="color:#e4e6eb;">${escapeHtml(tag)}</span>
      </div>
      <script>
        (function() {
          const btnTrue = document.getElementById('cpopup-set-true');
          const btnFalse = document.getElementById('cpopup-set-false');
          let curVal = ${isBoolTrue};
          btnTrue.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnTrue, 
              () => window._cpopupWriteBool('${tag}', true, null, curVal, '${displayName.replace(/'/g, "\\'")}'), 
              '${tag}', true, 'START komutu gönderildi', 'START başarısız');
            if (ok) { curVal = true; window._cpopupSetToggle(btnTrue, btnFalse, true); }
          };
          btnFalse.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnFalse,
              () => window._cpopupWriteBool('${tag}', false, null, curVal, '${displayName.replace(/'/g, "\\'")}'),
              '${tag}', false, 'STOP komutu gönderildi', 'STOP başarısız');
            if (ok) { curVal = false; window._cpopupSetToggle(btnTrue, btnFalse, false); }
          };
        })();
      </script>
    `;
  });

  // ============================================================
  // POPUP TİPİ: control-setreal
  // Real değer yazma — Input + Send butonu
  // ============================================================

  registerPopupType('control-setreal', async (config) => {
    const tag = config.rootTag;
    const labels = config.labels || {};
    const displayName = config.title || config.displayName || tag;

    let currentValue = await readTagValue(tag);
    if (currentValue === null) currentValue = '';

    return `
      <div class="cpopup-input-group">
        <label>${escapeHtml(labels.inputLabel || 'Set Değeri')}</label>
        <input type="number" id="cpopup-real-input" value="${currentValue}" step="any" />
      </div>
      <div class="cpopup-footer">
        <button class="cpopup-btn cpopup-btn-apply" id="cpopup-real-send">
          <i class="fas fa-paper-plane"></i> ${escapeHtml(labels.sendBtn || 'GÖNDER')}
        </button>
      </div>
      <div style="text-align:center; font-size:12px; color:#8a8ea8; margin-top:8px;">
        Tag: <span style="color:#e4e6eb;">${escapeHtml(tag)}</span>
      </div>
      <script>
        (function() {
          const initVal = '${currentValue}';
          document.getElementById('cpopup-real-send').onclick = async () => {
            const val = document.getElementById('cpopup-real-input').value;
            if (val === '' || isNaN(val)) { window._cpopupNotify('Geçerli bir sayı giriniz', 'warning'); return; }
            const btn = document.getElementById('cpopup-real-send');
            const ok = await window._cpopupWriteAndVerify(btn,
              () => window._cpopupWriteReal('${tag}', val, null, initVal !== '' ? parseFloat(initVal) : null, '${displayName.replace(/'/g, "\\'")}'),
              null, null, 'Değer gönderildi: ' + val, 'Değer gönderilemedi');
            if (ok) window._cpopupManager.close();
          };
        })();
      </script>
    `;
  });

  // ============================================================
  // POPUP TİPİ: control-onoff
  // Motor On/Off — Start/Stop butonları (suffix: _Mstr)
  // ============================================================

  registerPopupType('control-onoff', async (config) => {
    const root = config.rootTag;
    const sfx = config.suffixes || {};
    const startTag = root + (sfx.start || '_Mstr');
    const labels = config.labels || {};
    const displayName = config.title || config.displayName || root;

    let isRunning = null;
    const rawVal = await readTagValue(startTag);
    if (rawVal !== null) {
      isRunning = rawVal === true || rawVal === 'true' || rawVal === 1;
    }

    return `
      <div class="cpopup-btn-row">
        <button class="cpopup-btn cpopup-btn-start ${isRunning === true ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-onoff-start">
          <i class="fas fa-play"></i> ${escapeHtml(labels.startBtn || 'START')}
        </button>
        <button class="cpopup-btn cpopup-btn-stop ${isRunning === false ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-onoff-stop">
          <i class="fas fa-stop"></i> ${escapeHtml(labels.stopBtn || 'STOP')}
        </button>
      </div>
      <div style="text-align:center; font-size:12px; color:#8a8ea8;">
        Tag: <span style="color:#e4e6eb;">${escapeHtml(startTag)}</span>
      </div>
      <script>
        (function() {
          const btnStart = document.getElementById('cpopup-onoff-start');
          const btnStop = document.getElementById('cpopup-onoff-stop');
          let curVal = ${isRunning === true};
          btnStart.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnStart,
              () => window._cpopupWriteBool('${startTag}', true, null, curVal, '${displayName.replace(/'/g, "\\'")}'),
              '${startTag}', true, 'START komutu gönderildi', 'START başarısız');
            if (ok) { curVal = true; window._cpopupSetToggle(btnStart, btnStop, true); }
          };
          btnStop.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnStop,
              () => window._cpopupWriteBool('${startTag}', false, null, curVal, '${displayName.replace(/'/g, "\\'")}'),
              '${startTag}', false, 'STOP komutu gönderildi', 'STOP başarısız');
            if (ok) { curVal = false; window._cpopupSetToggle(btnStart, btnStop, false); }
          };
        })();
      </script>
    `;
  });

  // ============================================================
  // POPUP TİPİ: control-onoff-auto
  // Modlu motor — Auto/Man + Start/Stop (suffix: _Au, _Mstr)
  // ============================================================

  registerPopupType('control-onoff-auto', async (config) => {
    const root = config.rootTag;
    const sfx = config.suffixes || {};
    const autoTag = root + (sfx.auto || '_Au');
    const startTag = root + (sfx.start || '_Mstr');
    const labels = config.labels || {};
    const displayName = config.title || config.displayName || root;

    let isAuto = null, isRunning = null;
    const autoRaw = await readTagValue(autoTag);
    if (autoRaw !== null) isAuto = autoRaw === true || autoRaw === 'true' || autoRaw === 1;
    const startRaw = await readTagValue(startTag);
    if (startRaw !== null) isRunning = startRaw === true || startRaw === 'true' || startRaw === 1;

    return `
      <div class="cpopup-btn-row">
        <button class="cpopup-btn cpopup-btn-auto ${isAuto === true ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-auto-btn">
          <i class="fas fa-robot"></i> ${escapeHtml(labels.autoBtn || 'AUTO')}
        </button>
        <button class="cpopup-btn cpopup-btn-man ${isAuto === false ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-man-btn">
          <i class="fas fa-hand-paper"></i> ${escapeHtml(labels.manBtn || 'MAN')}
        </button>
      </div>
      <div class="cpopup-divider"></div>
      <div class="cpopup-btn-row">
        <button class="cpopup-btn cpopup-btn-start ${isRunning === true ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-start-btn">
          <i class="fas fa-play"></i> ${escapeHtml(labels.startBtn || 'START')}
        </button>
        <button class="cpopup-btn cpopup-btn-stop ${isRunning === false ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-stop-btn">
          <i class="fas fa-stop"></i> ${escapeHtml(labels.stopBtn || 'STOP')}
        </button>
      </div>
      <script>
        (function() {
          const btnAuto = document.getElementById('cpopup-auto-btn');
          const btnMan = document.getElementById('cpopup-man-btn');
          const btnStart = document.getElementById('cpopup-start-btn');
          const btnStop = document.getElementById('cpopup-stop-btn');
          let curAuto = ${isAuto === true};
          let curRun = ${isRunning === true};
          btnAuto.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnAuto,
              () => window._cpopupWriteBool('${autoTag}', true, null, curAuto, '${displayName.replace(/'/g, "\\'")}'),
              '${autoTag}', true, 'AUTO moda geçildi', 'AUTO mod başarısız');
            if (ok) { curAuto = true; window._cpopupSetToggle(btnAuto, btnMan, true); }
          };
          btnMan.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnMan,
              () => window._cpopupWriteBool('${autoTag}', false, null, curAuto, '${displayName.replace(/'/g, "\\'")}'),
              '${autoTag}', false, 'MANUEL moda geçildi', 'MANUEL mod başarısız');
            if (ok) { curAuto = false; window._cpopupSetToggle(btnAuto, btnMan, false); }
          };
          btnStart.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnStart,
              () => window._cpopupWriteBool('${startTag}', true, null, curRun, '${displayName.replace(/'/g, "\\'")}'),
              '${startTag}', true, 'START komutu gönderildi', 'START başarısız');
            if (ok) { curRun = true; window._cpopupSetToggle(btnStart, btnStop, true); }
          };
          btnStop.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnStop,
              () => window._cpopupWriteBool('${startTag}', false, null, curRun, '${displayName.replace(/'/g, "\\'")}'),
              '${startTag}', false, 'STOP komutu gönderildi', 'STOP başarısız');
            if (ok) { curRun = false; window._cpopupSetToggle(btnStart, btnStop, false); }
          };
        })();
      </script>
    `;
  });

  // ============================================================
  // POPUP TİPİ: control-setpoint
  // Tek set değeri — Input + Uygula (suffix: _Aval)
  // ============================================================

  registerPopupType('control-setpoint', async (config) => {
    const root = config.rootTag;
    const sfx = config.suffixes || {};
    const setTag = root + (sfx.autoSP || '_Aval');
    const labels = config.labels || {};
    const displayName = config.title || config.displayName || root;

    let currentValue = await readTagValue(setTag);
    if (currentValue === null) currentValue = '';

    return `
      <div class="cpopup-input-group">
        <label>${escapeHtml(labels.inputLabel || 'Set Point')}</label>
        <input type="number" id="cpopup-sp-input" value="${currentValue}" step="any" />
      </div>
      <div class="cpopup-footer">
        <button class="cpopup-btn cpopup-btn-apply" id="cpopup-sp-apply">
          <i class="fas fa-check"></i> ${escapeHtml(labels.applyBtn || 'UYGULA')}
        </button>
      </div>
      <script>
        (function() {
          const initVal = '${currentValue}';
          document.getElementById('cpopup-sp-apply').onclick = async () => {
            const val = document.getElementById('cpopup-sp-input').value;
            if (val === '' || isNaN(val)) { window._cpopupNotify('Geçerli bir sayı giriniz', 'warning'); return; }
            if (val === initVal) { window._cpopupNotify('Değer değişmedi', 'info'); return; }
            const btn = document.getElementById('cpopup-sp-apply');
            const ok = await window._cpopupWriteAndVerify(btn,
              () => window._cpopupWriteReal('${setTag}', val, null, initVal !== '' ? parseFloat(initVal) : null, '${displayName.replace(/'/g, "\\'")}'),
              null, null, 'Set değeri gönderildi: ' + val, 'Set değeri gönderilemedi');
            if (ok) window._cpopupManager.close();
          };
        })();
      </script>
    `;
  });

  // ============================================================
  // POPUP TİPİ: control-proportional
  // Tam oransal kontrol — Auto/Man, Start/Stop, AutoSP, ManSP, Reset
  // ============================================================

  registerPopupType('control-proportional', async (config) => {
    const root = config.rootTag;
    const sfx = config.suffixes || {};
    const autoTag = root + (sfx.auto || '_Au');
    const startTag = root + (sfx.start || '_Mstr');
    const autoSPTag = root + (sfx.autoSP || '_Aval');
    const manSPTag = root + (sfx.manSP || '_Mval');
    const resetTag = root + (sfx.reset || '_Res');
    const labels = config.labels || {};
    const displayName = config.title || config.displayName || root;

    let isAuto = null, isRunning = null, autoSP = '', manSP = '';
    const autoRaw = await readTagValue(autoTag);
    if (autoRaw !== null) isAuto = autoRaw === true || autoRaw === 'true' || autoRaw === 1;
    const startRaw = await readTagValue(startTag);
    if (startRaw !== null) isRunning = startRaw === true || startRaw === 'true' || startRaw === 1;
    const aspRaw = await readTagValue(autoSPTag);
    if (aspRaw !== null) autoSP = aspRaw;
    const mspRaw = await readTagValue(manSPTag);
    if (mspRaw !== null) manSP = mspRaw;

    return `
      <div class="cpopup-btn-row">
        <button class="cpopup-btn cpopup-btn-auto ${isAuto === true ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-pr-auto">
          <i class="fas fa-robot"></i> ${escapeHtml(labels.autoBtn || 'AUTO')}
        </button>
        <button class="cpopup-btn cpopup-btn-man ${isAuto === false ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-pr-man">
          <i class="fas fa-hand-paper"></i> ${escapeHtml(labels.manBtn || 'MAN')}
        </button>
      </div>
      <div class="cpopup-divider"></div>
      <div class="cpopup-btn-row">
        <button class="cpopup-btn cpopup-btn-start ${isRunning === true ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-pr-start">
          <i class="fas fa-play"></i> ${escapeHtml(labels.startBtn || 'START')}
        </button>
        <button class="cpopup-btn cpopup-btn-stop ${isRunning === false ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-pr-stop">
          <i class="fas fa-stop"></i> ${escapeHtml(labels.stopBtn || 'STOP')}
        </button>
      </div>
      <div class="cpopup-divider"></div>
      <div style="display:flex; gap:10px;">
        <div class="cpopup-input-group" style="flex:1">
          <label>Auto SP</label>
          <input type="number" id="cpopup-pr-autosp" value="${autoSP}" step="any" />
        </div>
        <div class="cpopup-input-group" style="flex:1">
          <label>Man SP</label>
          <input type="number" id="cpopup-pr-mansp" value="${manSP}" step="any" />
        </div>
      </div>
      <div class="cpopup-footer">
        <button class="cpopup-btn cpopup-btn-reset" id="cpopup-pr-reset">
          <i class="fas fa-undo"></i> ${escapeHtml(labels.resetBtn || 'RESET')}
        </button>
        <button class="cpopup-btn cpopup-btn-apply" id="cpopup-pr-apply">
          <i class="fas fa-check"></i> ${escapeHtml(labels.applyBtn || 'UYGULA')}
        </button>
      </div>
      <script>
        (function() {
          const initAutoSP = '${autoSP}';
          const initManSP = '${manSP}';
          const btnAuto = document.getElementById('cpopup-pr-auto');
          const btnMan = document.getElementById('cpopup-pr-man');
          const btnStart = document.getElementById('cpopup-pr-start');
          const btnStop = document.getElementById('cpopup-pr-stop');
          let curAuto = ${isAuto === true};
          let curRun = ${isRunning === true};
          const dn = '${displayName.replace(/'/g, "\\'")}';

          btnAuto.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnAuto,
              () => window._cpopupWriteBool('${autoTag}', true, null, curAuto, dn),
              '${autoTag}', true, 'AUTO moda geçildi', 'AUTO mod başarısız');
            if (ok) { curAuto = true; window._cpopupSetToggle(btnAuto, btnMan, true); }
          };
          btnMan.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnMan,
              () => window._cpopupWriteBool('${autoTag}', false, null, curAuto, dn),
              '${autoTag}', false, 'MANUEL moda geçildi', 'MANUEL mod başarısız');
            if (ok) { curAuto = false; window._cpopupSetToggle(btnAuto, btnMan, false); }
          };
          btnStart.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnStart,
              () => window._cpopupWriteBool('${startTag}', true, null, curRun, dn),
              '${startTag}', true, 'START komutu gönderildi', 'START başarısız');
            if (ok) { curRun = true; window._cpopupSetToggle(btnStart, btnStop, true); }
          };
          btnStop.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnStop,
              () => window._cpopupWriteBool('${startTag}', false, null, curRun, dn),
              '${startTag}', false, 'STOP komutu gönderildi', 'STOP başarısız');
            if (ok) { curRun = false; window._cpopupSetToggle(btnStart, btnStop, false); }
          };

          document.getElementById('cpopup-pr-reset').onclick = async () => {
            const btn = document.getElementById('cpopup-pr-reset');
            const ok = await window._cpopupWriteAndVerify(btn,
              () => window._cpopupWriteBool('${resetTag}', true, null, false, dn),
              null, null, 'RESET komutu gönderildi', 'RESET başarısız');
          };

          document.getElementById('cpopup-pr-apply').onclick = async () => {
            const newAutoSP = document.getElementById('cpopup-pr-autosp').value;
            const newManSP = document.getElementById('cpopup-pr-mansp').value;
            let changed = false;
            if (newAutoSP !== initAutoSP && newAutoSP !== '') {
              await window._cpopupWriteReal('${autoSPTag}', newAutoSP, null, initAutoSP !== '' ? parseFloat(initAutoSP) : null, dn);
              window._cpopupNotify('Auto SP: ' + newAutoSP + ' olarak set edildi', 'success');
              changed = true;
            }
            if (newManSP !== initManSP && newManSP !== '') {
              await window._cpopupWriteReal('${manSPTag}', newManSP, null, initManSP !== '' ? parseFloat(initManSP) : null, dn);
              window._cpopupNotify('Man SP: ' + newManSP + ' olarak set edildi', 'success');
              changed = true;
            }
            if (!changed) {
              window._cpopupNotify('Herhangi bir değer değişikliği yapılmadı', 'info');
            } else {
              window._cpopupManager.close();
            }
          };
        })();
      </script>
    `;
  });

  // ============================================================
  // POPUP TİPİ: sensor-chart
  // Sensör için sekmeli popup:
  //   Tab 1 — Trend: son 24h saatlik veri (Chart.js line chart)
  //   Tab 2 — Kalibrasyon: (Faz 2/3/4'te uygulanacak, şimdilik placeholder)
  // ============================================================
  registerPopupType('sensor-chart', async (config) => {
    const rootTag = config.rootTag || '';
    const displayName = config.title || config.displayName || rootTag;
    const calibMode = config.calibrationMode || 'none';
    const calib = config.calibration || {};

    // Admin yetkisi var mı? (kalibrasyon sekmesi sadece admin için)
    let isAdmin = false;
    try {
      if (window.Auth && typeof window.Auth.isAdmin === 'function') {
        isAdmin = !!window.Auth.isAdmin();
      } else if (window.Auth && typeof window.Auth.getUserInfo === 'function') {
        const info = window.Auth.getUserInfo();
        const role = info && (info.Role || info.role || '');
        isAdmin = /admin/i.test(String(role));
      }
    } catch (e) { isAdmin = false; }

    // Kalibrasyon sekmesi içeriği (Faz 2: bilgi/preview; Faz 3/4: gerçek formlar)
    function buildCalibTab() {
      if (!isAdmin) {
        return `<div style="padding:18px;text-align:center;color:#8a8ea8;">
           <i class="fas fa-lock" style="font-size:24px;margin-bottom:8px;display:block;color:#3a3d52;"></i>
           <div style="font-size:13px;">Bu sekme yalnızca admin yetkisiyle görünür.</div>
         </div>`;
      }
      if (calibMode === 'none') {
        return `<div style="padding:18px;text-align:center;color:#8a8ea8;">
           <i class="fas fa-info-circle" style="font-size:24px;margin-bottom:8px;display:block;color:#3a3d52;"></i>
           <div style="font-size:13px;">Bu sensör için kalibrasyon yapılandırılmamış.</div>
           <div style="font-size:11px;margin-top:6px;">Admin panel → element seç → Kalibrasyon Modu</div>
         </div>`;
      }
      if (calibMode === 'soft') {
        const m  = calib.multiplier != null ? calib.multiplier : 1;
        const o  = calib.offset != null ? calib.offset : 0;
        const r1 = calib.minRaw != null ? calib.minRaw : 0;
        const r2 = calib.maxRaw != null ? calib.maxRaw : 0;
        const e1 = calib.minEng != null ? calib.minEng : 0;
        const e2 = calib.maxEng != null ? calib.maxEng : 0;
        return `<div style="padding:14px;">
           <div style="color:#3399ff;font-size:12px;font-weight:600;margin-bottom:8px;">
             <i class="fas fa-sliders-h"></i> SOFT KALİBRASYON (mevcut değerler — düzenleme Faz 3'te)
           </div>
           <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;font-size:12px;">
             <div style="background:#262940;padding:8px;border-radius:4px;"><div style="color:#8a8ea8;font-size:10px;">Multiplier</div><div style="color:#fff;font-size:14px;">${m}</div></div>
             <div style="background:#262940;padding:8px;border-radius:4px;"><div style="color:#8a8ea8;font-size:10px;">Offset</div><div style="color:#fff;font-size:14px;">${o}</div></div>
             <div style="background:#262940;padding:8px;border-radius:4px;"><div style="color:#8a8ea8;font-size:10px;">Min Raw → Max Raw</div><div style="color:#fff;font-size:14px;">${r1} → ${r2}</div></div>
             <div style="background:#262940;padding:8px;border-radius:4px;"><div style="color:#8a8ea8;font-size:10px;">Min Eng → Max Eng</div><div style="color:#fff;font-size:14px;">${e1} → ${e2}</div></div>
           </div>
         </div>`;
      }
      if (calibMode === 'plc') {
        const rootSuffix = calib.rootSuffix || '';
        const allFields = Array.isArray(calib.fields) ? calib.fields : [];
        // Bool tipler kalibrasyon write UI'ında gösterilmez (real/int)
        const fields = allFields.filter(f => (f.type || 'real').toLowerCase() !== 'bool');
        const skippedBoolCount = allFields.length - fields.length;
        const rootCalculated = rootSuffix && rootTag.endsWith(rootSuffix)
          ? rootTag.slice(0, -rootSuffix.length) : rootTag;
        const fieldsHtml = fields.length === 0
          ? `<div style="color:#e55353;font-size:12px;text-align:center;padding:10px;">⚠️ Yazılabilir alan yok. Admin panelden ekle.</div>`
          : `<table style="width:100%;font-size:12px;border-collapse:collapse;">
               <thead><tr style="color:#8a8ea8;font-size:11px;text-align:left;">
                 <th style="padding:6px 6px;">Etiket</th>
                 <th style="padding:6px 6px;width:140px;">Canlı Değer</th>
                 <th style="padding:6px 6px;width:130px;">Yeni Değer</th>
                 <th style="padding:6px 6px;width:90px;text-align:right;">Aksiyon</th>
               </tr></thead>
               <tbody>${fields.map((f, idx) => {
                 const writeTag = rootCalculated + (f.suffix || '');
                 const ftype = (f.type || 'real').toLowerCase();
                 const step = ftype === 'int' ? '1' : 'any';
                 return `
                 <tr style="border-top:1px solid #333654;" data-calib-row="${idx}" data-tag="${escapeHtml(writeTag)}" data-type="${escapeHtml(ftype)}">
                   <td style="padding:6px 6px;color:#fff;">
                     <div>${escapeHtml(f.label || '')}</div>
                     <div style="font-size:10px;color:#3399ff;font-family:monospace;">${escapeHtml(writeTag)}</div>
                   </td>
                   <td style="padding:6px 6px;">
                     <span class="cpopup-calib-live" data-row="${idx}" style="color:#2eb85c;font-family:monospace;font-size:13px;">—</span>
                   </td>
                   <td style="padding:6px 6px;">
                     <input type="number" step="${step}" class="cpopup-calib-input" data-row="${idx}"
                       style="width:100%;padding:4px 6px;background:#1a1d35;color:#fff;border:1px solid #333654;border-radius:3px;font-size:12px;font-family:monospace;" />
                   </td>
                   <td style="padding:6px 6px;text-align:right;">
                     <button class="cpopup-calib-write btn btn-primary btn-sm" data-row="${idx}"
                       style="font-size:11px;padding:4px 10px;" disabled>
                       <i class="fas fa-save"></i> YAZ
                     </button>
                   </td>
                 </tr>`;
               }).join('')}</tbody>
             </table>`;
        const skipNote = skippedBoolCount > 0
          ? `<div style="font-size:10px;color:#f9b115;margin-top:6px;">ℹ️ ${skippedBoolCount} adet bool alan gizlendi (kalibrasyonda yazılmaz).</div>` : '';
        return `<div style="padding:14px;">
           <div style="color:#3399ff;font-size:12px;font-weight:600;margin-bottom:8px;display:flex;justify-content:space-between;align-items:center;">
             <span><i class="fas fa-microchip"></i> PLC KALİBRASYON</span>
             <span style="display:flex;gap:8px;align-items:center;">
               <span id="cpopup-calib-status" style="font-size:10px;color:#8a8ea8;font-weight:400;">Yükleniyor...</span>
               <button id="cpopup-calib-refresh" class="btn btn-sm btn-ghost-primary" style="font-size:10px;padding:2px 8px;" title="Canlı değerleri tekrar oku">
                 <i class="fas fa-sync-alt"></i>
               </button>
             </span>
           </div>
           <div style="font-size:11px;color:#8a8ea8;margin-bottom:8px;">
             Kök: <code style="color:#3399ff;">${escapeHtml(rootCalculated || '(?)')}</code>
             · Suffix: <code>${escapeHtml(rootSuffix || '(boş)')}</code>
           </div>
           ${fieldsHtml}
           ${skipNote}
         </div>`;
      }
      return '';
    }

    const calibTabHtml = buildCalibTab();

    return `
      <div style="min-width:780px;width:80vw;max-width:1100px;">
        <ul class="cpopup-tabs" style="display:flex;list-style:none;padding:0;margin:0 0 12px 0;border-bottom:1px solid #333654;gap:4px;">
          <li class="cpopup-tab active" data-tab="trend"
              style="padding:8px 16px;cursor:pointer;border-radius:6px 6px 0 0;background:#262940;color:#fff;font-size:13px;font-weight:600;">
            <i class="fas fa-chart-line" style="margin-right:6px;"></i>Trend (24h)
          </li>
          <li class="cpopup-tab" data-tab="calib"
              style="padding:8px 16px;cursor:pointer;border-radius:6px 6px 0 0;background:transparent;color:#8a8ea8;font-size:13px;font-weight:600;">
            <i class="fas fa-sliders-h" style="margin-right:6px;"></i>Kalibrasyon ${isAdmin ? '' : '<i class="fas fa-lock" style="font-size:10px;margin-left:4px;"></i>'}
          </li>
        </ul>

        <div id="cpopup-sensor-tab-trend" class="cpopup-tab-pane" style="display:block;">
          <div id="cpopup-sensor-chart-loading" style="text-align:center;padding:40px;color:#8a8ea8;">
            <span class="cpopup-spinner"></span>
            <div style="margin-top:10px;font-size:13px;">Son 24 saatlik veri yükleniyor...</div>
          </div>
          <div id="cpopup-sensor-chart-error" style="display:none;padding:20px;text-align:center;color:#e55353;font-size:13px;"></div>
          <div id="cpopup-sensor-chart-wrap" style="display:none;position:relative;height:380px;">
            <canvas id="cpopup-sensor-chart-canvas"></canvas>
          </div>
        </div>

        <div id="cpopup-sensor-tab-calib" class="cpopup-tab-pane" style="display:none;">
          ${calibTabHtml}
        </div>
      </div>

      <script>
        (function() {
          const rootTag = ${JSON.stringify(rootTag)};
          const displayName = ${JSON.stringify(displayName)};
          let chartInstance = null;

          // Tab değiştirme
          const tabs = document.querySelectorAll('.cpopup-tab');
          tabs.forEach(t => {
            t.onclick = () => {
              tabs.forEach(x => {
                x.classList.remove('active');
                x.style.background = 'transparent';
                x.style.color = '#8a8ea8';
              });
              t.classList.add('active');
              t.style.background = '#262940';
              t.style.color = '#fff';
              const which = t.getAttribute('data-tab');
              document.getElementById('cpopup-sensor-tab-trend').style.display = (which === 'trend') ? 'block' : 'none';
              document.getElementById('cpopup-sensor-tab-calib').style.display = (which === 'calib') ? 'block' : 'none';
            };
          });

          // Tarih formatı: "YYYY-MM-DD HH:mm" (mevcut chart endpoint formatı)
          function fmt(d) {
            const yy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            const hh = String(d.getHours()).padStart(2, '0');
            const mi = String(d.getMinutes()).padStart(2, '0');
            return yy + '-' + mm + '-' + dd + ' ' + hh + ':' + mi;
          }

          // 24h verisini çek
          async function loadTrend() {
            const loadingEl = document.getElementById('cpopup-sensor-chart-loading');
            const errorEl   = document.getElementById('cpopup-sensor-chart-error');
            const wrapEl    = document.getElementById('cpopup-sensor-chart-wrap');

            if (typeof Chart === 'undefined') {
              loadingEl.style.display = 'none';
              errorEl.style.display = 'block';
              errorEl.textContent = 'Chart.js yüklenmemiş.';
              return;
            }

            const toDate   = new Date();
            const fromDate = new Date(toDate.getTime() - 24 * 60 * 60 * 1000);

            try {
              const resp = await fetch('/api/v1/chart/multi-tags-data', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  tagNameArray: rootTag,
                  fromDate: fmt(fromDate),
                  toDate:   fmt(toDate),
                  freq: 'Hour'
                })
              });

              if (!resp.ok) {
                const errTxt = await resp.text().catch(() => '');
                throw new Error('API ' + resp.status + (errTxt ? ' — ' + errTxt.slice(0, 200) : ''));
              }
              const json = await resp.json();
              const data = (json && json.data) || [];

              if (!data.length) {
                loadingEl.style.display = 'none';
                errorEl.style.display = 'block';
                errorEl.textContent = 'Son 24 saat için veri bulunamadı.';
                return;
              }

              // Bu tag'a ait satırları filtrele (API tek tag istesek de bazen başka tag dönebilir)
              const rows = data.filter(r => !r.TagName || r.TagName === rootTag);
              let prevDay = '';
              const labels = rows.map(r => {
                const ts = r.Date || r.TimeStamp || r.date || r.timestamp;
                if (!ts) return '';
                // "YYYY-MM-DD HH:mm" veya ISO string -> gün değişimi varsa "DD/MM HH:mm", aksi halde "HH:mm"
                const m = String(ts).match(/(\\d{4})-(\\d{2})-(\\d{2})[ T](\\d{2}):(\\d{2})/);
                if (m) {
                  const dayKey = m[3] + '/' + m[2];
                  const hhmm = m[4] + ':' + m[5];
                  if (dayKey !== prevDay) {
                    prevDay = dayKey;
                    return dayKey + ' ' + hhmm;
                  }
                  return hhmm;
                }
                return String(ts);
              });
              const values = rows.map(r => {
                const v = (r.Value !== undefined) ? r.Value : (r.value !== undefined ? r.value : null);
                return v === null ? null : Number(v);
              });

              loadingEl.style.display = 'none';
              wrapEl.style.display = 'block';

              const canvas = document.getElementById('cpopup-sensor-chart-canvas');
              if (chartInstance) { chartInstance.destroy(); chartInstance = null; }

              chartInstance = new Chart(canvas.getContext('2d'), {
                type: 'line',
                data: {
                  labels: labels,
                  datasets: [{
                    label: displayName,
                    data: values,
                    borderColor: 'rgb(51, 153, 255)',
                    backgroundColor: 'rgba(51, 153, 255, 0.15)',
                    tension: 0.3,
                    pointRadius: 2,
                    pointHoverRadius: 5,
                    fill: true,
                    spanGaps: true
                  }]
                },
                options: {
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { labels: { color: '#e4e6eb' } },
                    tooltip: { mode: 'index', intersect: false }
                  },
                  scales: {
                    x: {
                      ticks: { color: '#8a8ea8', maxRotation: 0, autoSkip: true, maxTicksLimit: 12 },
                      grid:  { color: 'rgba(255,255,255,0.05)' }
                    },
                    y: {
                      ticks: { color: '#8a8ea8' },
                      grid:  { color: 'rgba(255,255,255,0.05)' }
                    }
                  }
                }
              });
            } catch (err) {
              console.error('[sensor-chart] Trend yüklenemedi:', err);
              loadingEl.style.display = 'none';
              errorEl.style.display = 'block';
              errorEl.textContent = 'Veri yüklenirken hata oluştu: ' + (err.message || err);
            }
          }

          loadTrend();

          // ============================================================
          // PLC Kalibrasyon — canlı okuma + yazma (Faz 4)
          // ============================================================
          const _calibMode = ${JSON.stringify(calibMode)};
          const _calibIsAdmin = ${JSON.stringify(isAdmin)};
          if (_calibMode === 'plc' && _calibIsAdmin) {
            const rows = Array.from(document.querySelectorAll('[data-calib-row]'));
            const statusEl = document.getElementById('cpopup-calib-status');
            const tabContainer = document.getElementById('cpopup-sensor-tab-calib');
            // Tag → row index map
            const tagsToRead = rows.map(r => r.getAttribute('data-tag'));
            const lastLiveValue = {}; // tag → number

            function setStatus(text, color) {
              if (statusEl) {
                statusEl.textContent = text;
                statusEl.style.color = color || '#8a8ea8';
              }
            }

            function fmtNum(v, type) {
              if (v === null || v === undefined || isNaN(v)) return '—';
              if (type === 'int') return String(Math.round(Number(v)));
              const n = Number(v);
              return (Math.abs(n) >= 1000 ? n.toFixed(2) : n.toFixed(3)).replace(/\.?0+$/, '');
            }

            function updateButtonState(row) {
              const idx = row.getAttribute('data-calib-row');
              const input = document.querySelector('.cpopup-calib-input[data-row="' + idx + '"]');
              const btn   = document.querySelector('.cpopup-calib-write[data-row="' + idx + '"]');
              if (!input || !btn) return;
              const tag = row.getAttribute('data-tag');
              const live = lastLiveValue[tag];
              const val = input.value.trim();
              if (val === '' || isNaN(Number(val))) { btn.disabled = true; return; }
              if (live != null && Number(val) === Number(live)) { btn.disabled = true; return; }
              btn.disabled = false;
            }

            async function readLiveValues() {
              if (!document.body.contains(tabContainer)) return false; // popup kapandı
              try {
                const resp = await window._cpopupReadTags(tagsToRead);
                if (resp && resp.success && resp.tags) {
                  rows.forEach(row => {
                    const idx = row.getAttribute('data-calib-row');
                    const tag = row.getAttribute('data-tag');
                    const type = row.getAttribute('data-type');
                    const entry = resp.tags[tag];
                    const v = entry && (entry.value !== undefined ? entry.value : entry);
                    const liveCell = document.querySelector('.cpopup-calib-live[data-row="' + idx + '"]');
                    if (v === null || v === undefined) {
                      if (liveCell) { liveCell.textContent = 'YOK'; liveCell.style.color = '#e55353'; }
                      return;
                    }
                    const num = Number(v);
                    if (isNaN(num)) {
                      if (liveCell) { liveCell.textContent = String(v); liveCell.style.color = '#f9b115'; }
                      return;
                    }
                    lastLiveValue[tag] = num;
                    if (liveCell) { liveCell.textContent = fmtNum(num, type); liveCell.style.color = '#2eb85c'; }
                    // İlk yükleme: input boşsa canlı değeri öneri olarak yerleştir
                    const input = document.querySelector('.cpopup-calib-input[data-row="' + idx + '"]');
                    if (input && input.value === '' && !input.dataset.userTouched) {
                      input.value = fmtNum(num, type);
                    }
                    updateButtonState(row);
                  });
                  setStatus('Canlı · ' + new Date().toLocaleTimeString('tr-TR'), '#2eb85c');
                  return true;
                } else {
                  setStatus('Okuma başarısız', '#e55353');
                }
              } catch (e) {
                console.error('[sensor-chart calib] read err:', e);
                setStatus('Bağlantı hatası', '#e55353');
              }
              return false;
            }

            // Input change → butonu enable/disable + userTouched işaretle
            document.querySelectorAll('.cpopup-calib-input').forEach(inp => {
              inp.addEventListener('input', () => {
                inp.dataset.userTouched = '1';
                const row = inp.closest('[data-calib-row]');
                if (row) updateButtonState(row);
              });
            });

            // Write butonları
            document.querySelectorAll('.cpopup-calib-write').forEach(btn => {
              btn.addEventListener('click', async () => {
                const idx = btn.getAttribute('data-row');
                const row = document.querySelector('[data-calib-row="' + idx + '"]');
                const input = document.querySelector('.cpopup-calib-input[data-row="' + idx + '"]');
                if (!row || !input) return;
                const tag = row.getAttribute('data-tag');
                const type = row.getAttribute('data-type');
                const rawVal = input.value.trim();
                if (rawVal === '' || isNaN(Number(rawVal))) {
                  window._cpopupNotify('Geçerli bir sayı giriniz', 'warning'); return;
                }
                const newVal = type === 'int' ? Math.round(Number(rawVal)) : Number(rawVal);
                const oldVal = lastLiveValue[tag];
                const labelCell = row.querySelector('td:first-child > div:first-child');
                const label = labelCell ? labelCell.textContent.trim() : tag;
                const writeFn = (type === 'int')
                  ? () => window._cpopupWriteInt(tag, newVal, null, oldVal, label)
                  : () => window._cpopupWriteReal(tag, newVal, null, oldVal, label);
                const ok = await window._cpopupWriteAndVerify(
                  btn, writeFn, tag, newVal,
                  label + ' = ' + newVal + ' yazıldı', label + ' yazılamadı'
                );
                if (ok) {
                  lastLiveValue[tag] = newVal;
                  delete input.dataset.userTouched;
                  // Yeniden oku
                  setTimeout(readLiveValues, 300);
                }
              });
            });

            // İlk okuma (popup açılışta tek seferlik)
            readLiveValues();

            // Yenile butonu
            const refreshBtn = document.getElementById('cpopup-calib-refresh');
            if (refreshBtn) {
              refreshBtn.addEventListener('click', () => {
                setStatus('Okunuyor...', '#8a8ea8');
                readLiveValues();
              });
            }
          }
        })();
      </script>
    `;
  });

  // ============================================================
  // Global Instance + Window Exports
  // ============================================================

  const manager = new ControlPopupManager();

  // Expose to window for inline scripts and external access
  window._cpopupManager = manager;
  window._cpopupWriteBool = plcWriteBool;
  window._cpopupWriteReal = plcWriteReal;
  window._cpopupWriteInt = plcWriteInt;
  window._cpopupReadTags = plcReadTags;
  window._cpopupWriteAndVerify = writeAndVerify;
  window._cpopupSetToggle = setToggleState;
  window._cpopupNotify = showNotify;
  window._cpopupConfirm = showConfirm;
  window._cpopupWriteBatch = plcWriteBatch;

  // Public API
  window.ControlPopupManager = manager;

  // ============================================================
  // WebSocket: Başka kullanıcıların PLC yazma bildirimlerini dinle
  // ============================================================
  function initWriteNotifications() {
    if (typeof io === 'undefined') return;
    try {
      const socket = io();
      socket.on('plc:write', function(data) {
        const currentUser = getCurrentUser();
        if (data.user && data.user !== currentUser) {
          showNotify(`${data.user} → ${data.tagName} = ${data.value}`, 'info');
        }
      });
    } catch(e) { /* Socket.IO bağlantısı yoksa sessizce geç */ }
  }

  // Sayfa yüklenince başlat
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWriteNotifications);
  } else {
    initWriteNotifications();
  }

})();
